import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  ArrowBack,
  LocationOn,
  Business,
  AttachMoney,
  Work,
  School,
  BusinessCenter
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { apiUrl } from "../config/api";
import useSEO from "../hooks/useSEO";

const EmployerJobDetail = () => {
  useSEO({ title: 'Управление вакансией | JobMD' });
  const navigate = useNavigate();
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadJobDetails();
  }, [id]);

  const loadJobDetails = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem("userId");
      const url = `${apiUrl("employer_jobs.php")}?action=get&id=${id}${userId ? `&userId=${userId}` : ""}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setJob(data.job);
      } else {
        setError(data.message || "Вакансия не найдена");
      }
    } catch (err) {
      setError("Ошибка загрузки вакансии: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatSalary = (job) => {
    if (!job.salary_from && !job.salary_to) return "Зарплата не указана";
    
    const from = job.salary_from ? `${job.salary_from}` : "";
    const to = job.salary_to ? `${job.salary_to}` : "";
    const currency = job.salary_currency || "USD";
    
    if (from && to) {
      return `${from} - ${to} ${currency}`;
    } else if (from) {
      return `от ${from} ${currency}`;
    } else if (to) {
      return `до ${to} ${currency}`;
    }
    
    return "Зарплата не указана";
  };

  const getEmploymentTypeText = (type) => {
    switch (type) {
      case 'full-time': return 'Полный день';
      case 'part-time': return 'Частичная занятость';
      case 'contract': return 'Контракт';
      case 'internship': return 'Стажировка';
      case 'remote': return 'Удаленная работа';
      case 'hybrid': return 'Гибридный формат';
      default: return type;
    }
  };

  const getExperienceText = (level) => {
    switch (level) {
      case 'intern': return 'Стажер';
      case 'junior': return 'Junior';
      case 'middle': return 'Middle';
      case 'senior': return 'Senior';
      case 'lead': return 'Lead';
      case 'any': return 'Любой опыт';
      default: return level;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !job) {
    return (
      <Box sx={{ maxWidth: "1200px", mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || "Вакансия не найдена"}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/employer-jobs")}
        >
          Вернуться к списку вакансий
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Кнопка назад */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate("/employer-jobs")}
        sx={{ mb: 3 }}
      >
        Назад к вакансиям
      </Button>

      <Card>
        <CardContent>
          {/* Заголовок */}
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {job.title}
          </Typography>
          
          {/* Компания */}
          <Box display="flex" alignItems="center" mb={3}>
            <Business sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h6" color="primary">
              {job.company_name}
            </Typography>
          </Box>

          {/* Основная информация */}
          <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
            <Chip 
              icon={<LocationOn />} 
              label={job.location_city || "Не указано"} 
            />
            <Chip 
              icon={<Work />} 
              label={getEmploymentTypeText(job.employment_type)} 
              color="primary"
            />
            <Chip 
              icon={<BusinessCenter />} 
              label={getExperienceText(job.experience_level)} 
            />
            <Chip 
              icon={<AttachMoney />} 
              label={formatSalary(job)} 
              color="success"
            />
            {job.remote_allowed && (
              <Chip 
                label="Удаленно" 
                color="info"
              />
            )}
          </Stack>

          {/* Описание */}
          <Box mb={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Описание
            </Typography>
            <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
              {job.description}
            </Typography>
          </Box>

          {/* Требования */}
          {job.requirements && (
            <Box mb={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Требования
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {job.requirements}
              </Typography>
            </Box>
          )}

          {/* Образование */}
          {job.education_level && job.education_level !== 'none' && (
            <Box mb={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                <School sx={{ verticalAlign: "middle", mr: 1 }} />
                Образование
              </Typography>
              <Typography variant="body1">
                {job.education_level === 'bachelor' ? 'Бакалавр' :
                 job.education_level === 'master' ? 'Магистр' :
                 job.education_level === 'phd' ? 'Кандидат наук' :
                 job.education_level}
              </Typography>
            </Box>
          )}

          {/* Контакты */}
          {(job.contact_email || job.contact_phone) && (
            <Box mb={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Контакты
              </Typography>
              <Stack spacing={1}>
                {job.contact_email && (
                  <Typography variant="body1">
                    Email: {job.contact_email}
                  </Typography>
                )}
                {job.contact_phone && (
                  <Typography variant="body1">
                    Телефон: {job.contact_phone}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* Кнопка отклика */}
          <Box mt={4} textAlign="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                const userId = localStorage.getItem("userId");
                if (userId) {
                  alert("Функция отклика в разработке");
                } else {
                  alert("Для отклика необходимо авторизоваться");
                  navigate("/login");
                }
              }}
            >
              Откликнуться на вакансию
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmployerJobDetail;